import { baseUrl, client } from "@/shared/api/newsquawk-headlines";
import { SearchHeadlinesParams } from "./types";
import { quotePhrases } from "@/shared/utils/quotePhrase";
import {
  extractKeywordsFromPortfolios,
  extractTickersFromPortfolios,
  groupPortfoliosByType,
} from "@/widget-components/firehose-widgets/shared/hooks/searchUtils";

export enum Endpoints {
  GetHeadlines = "/headline/headlines/list",
  GetHeadlineById = "/headline",
  SearchHeadlines = "/headline/search/full_text",
  DeleteHeadline = "/headline",
}

export enum PublicEndpoints {
  GetDelayedHeadlines = "/headline/headlines/list-delayed",
}

export const EventEndpoints = {
  HeadlineEvent: {
    eventType: "message",
    endpoint: `${baseUrl}/event/sse`,
  },
};

// Build search term with proper grouping for typed keywords and keyword watchlists
// Each keyword watchlist is kept as a group, and keywordWatchlistOp determines how they combine
// e.g., AND mode: "(bitcoin) AND (Fed OR FOMC) AND (Putin OR Xi)"
// e.g., OR mode: "bitcoin OR Fed OR FOMC OR Putin OR Xi"
const buildSearchTerm = (
  typedKeyword: string | undefined,
  keywordWatchlists: { name: string; keywords: string[] }[],
  keywordWatchlistOp: string
): string => {
  const hasTypedKeyword = typedKeyword && typedKeyword.trim();
  const watchlistsWithKeywords = keywordWatchlists.filter((w) => w.keywords.length > 0);
  const hasWatchlists = watchlistsWithKeywords.length > 0;

  // Collect all parts that need to be combined
  const parts: string[] = [];

  if (hasTypedKeyword) {
    parts.push(typedKeyword.trim());
  }

  if (keywordWatchlistOp === "AND") {
    // AND mode: each watchlist becomes a grouped part
    // e.g., "(Fed OR FOMC OR Powell)" for Central Banks
    watchlistsWithKeywords.forEach((watchlist) => {
      parts.push(`(${quotePhrases(watchlist.keywords).join(" OR ")})`);
    });

    if (parts.length === 0) return "";
    if (parts.length === 1) return parts[0];

    // Wrap typed keyword in parens if combining with watchlists
    if (hasTypedKeyword && hasWatchlists) {
      parts[0] = `(${parts[0]})`;
    }

    return parts.join(" AND ");
  } else {
    // OR mode: flatten all keywords together
    // Quoted so a two-word keyword stays one phrase: unquoted, the backend
    // splits on whitespace and ORs the words, so "rate cut" matched anything
    // containing "rate".
    watchlistsWithKeywords.forEach((watchlist) => {
      parts.push(...quotePhrases(watchlist.keywords));
    });

    return parts.join(" OR ");
  }
};

export const searchHeadlines = (params: SearchHeadlinesParams) => {
  const {
    query = "",
    rows = 10,
    exactMatch = false,
    advancedFilter,
    portfolios,
    cursor,
    signal,
  } = params;

  const tagWatchlistTagIds = (advancedFilter?.tagsWatchlists || []).flatMap(
    (tw) => tw.tags
  );
  const tagIds = [
    ...(advancedFilter?.categories || []),
    ...tagWatchlistTagIds,
  ];

  const allPortfolios = [
    ...(portfolios || []),
    ...(advancedFilter?.portfolios || []),
  ];

  // Group portfolios to separate keyword-only watchlists
  const groupedPortfolios = groupPortfoliosByType(allPortfolios);

  // Tickers come from ticker-only and mixed watchlists
  const tickers = extractTickersFromPortfolios([
    ...groupedPortfolios.tickerOnly,
    ...groupedPortfolios.mixed,
  ]);

  // Keep keyword-only watchlists as separate groups for AND/OR logic
  const keywordWatchlists = groupedPortfolios.keywordOnly.map((p) => ({
    name: p.name,
    keywords: p.keywords || [],
  }));

  // Keywords from mixed watchlists (these are OR'd with everything)
  const mixedKeywords = extractKeywordsFromPortfolios(groupedPortfolios.mixed);

  const keywordWatchlistOp = advancedFilter?.keywordWatchlistOp || "OR";

  // Build the main keyword search term with proper AND/OR grouping
  const keywordSearchTerm = buildSearchTerm(
    advancedFilter?.searchKeyword,
    keywordWatchlists,
    keywordWatchlistOp
  );

  // Combine with query and mixed keywords (always OR)
  const finalSearchTerm = [query, keywordSearchTerm, ...mixedKeywords]
    .filter(Boolean)
    .join(" OR ");

  const filterMode = advancedFilter?.filterMode || "OR";

  const keywordOp = advancedFilter?.keywordOp || "OR";

  return client.post(
    Endpoints.SearchHeadlines,
    {
      search_term: finalSearchTerm,
      rows,
      tags: tagIds,
      tickers,
      exact_match: exactMatch,
      filter_mode: filterMode.toUpperCase(),
      keyword_op: keywordOp?.toUpperCase(),
      cursor: cursor,
      date_range: {
        start_date: params.dateRange?.startDate,
        end_date: params.dateRange?.endDate,
      },
    },
    {
      signal,
    }
  );
};

export const searchHeadlinesDelayed = ({
  query = "",
  rows = 10,
  tagIds = [],
}: {
  query?: string;
  rows?: number;
  tagIds?: string[];
}) => {
  return client.get(PublicEndpoints.GetDelayedHeadlines, {
    params: { search_term: query, rows, tags: tagIds },
    paramsSerializer: {
      indexes: null,
    },
  });
};
